// Customizable Area Start
import React from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  styled,
  Dialog,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TwoFAConfirmationController, {
  Props,
  configJSON,
} from "./TwoFAConfirmationController.web";
import ConditionalRenderer from "../../../components/src/ConditionalRenderer.web";
import CustomButton from "../../../components/src/CustomButton.web";
// Customizable Area End

export default class TwoFAConfirmation extends TwoFAConfirmationController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  render() {
    // Customizable Area Start
    const { isOpen, phoneNumber, handleClose } = this.props;
    const { otpFields, otpError, isVerifyingPhone, isSendingOtp, remainingTime } = this.state;

    return (
      <OTPDialogWrapper
        data-test-id="otp-modal"
        open={isOpen}
        onClose={handleClose}
      >
        <Box display="flex" justifyContent="flex-end">
          <OTPCloseButton data-test-id="otp-close-button" onClick={handleClose}>
            <CloseRoundedIcon />
          </OTPCloseButton>
        </Box>
        <ContentWrapper>
          <MainTitle component="h2">
            {configJSON.otpVerificationTitle}
          </MainTitle>
          <ModalDescription>
            {configJSON.otpVerificationDescription}{" "}
            <ModalDescription>
              {this.COUNTRY_CODE_PREFIX} {phoneNumber}
            </ModalDescription>
          </ModalDescription>
          <FieldsContentWrapper>
            <Box sx={{ display: "flex", gap: "10px", marginTop: "15px" }}>
              {otpFields.map((_: string, index: number) => (
                <OTPField
                  key={index}
                  autoComplete="off"
                  type="tel"
                  inputMode="numeric"
                  autoFocus={index === 0}
                  value={otpFields[index]}
                  inputProps={{ maxLength: 1 }}
                  data-test-id="otp-input-field"
                  inputRef={this.inputRefs[index]}
                  onChange={(event) => this.handleOtpChange(index, event)}
                  onKeyDown={(event) => this.handleOtpKeyDown(index, event)}
                />
              ))}
            </Box>
            <Box
              display="flex"
              gap="40px"
              alignItems="center"
              justifyContent="space-between"
            >
              <CustomGrayText
                sx={{ visibility: remainingTime > 0 ? "visible" : "hidden" }}
              >
                {configJSON.timeRemainingText} 00:
                {this.formatTime(remainingTime)}
              </CustomGrayText>
              <ResendBtn
                data-test-id="otp-resend-button"
                disabled={remainingTime > 0 || isSendingOtp}
                onClick={this.handleSendOtp}
              >
                {configJSON.resendOtpText}
              </ResendBtn>
            </Box>
            <ConditionalRenderer isVisible={!!otpError}>
              <ErrorText data-test-id="otp-error">{otpError}</ErrorText>
            </ConditionalRenderer>
          </FieldsContentWrapper>
          <CustomButton
            fullWidth
            loaderSize={22}
            data-test-id="otp-submit-button"
            customVariant="filled"
            sx={{ maxWidth: "290px", height: "56px", mb: "15px", mt: "25px" }}
            isLoading={this.state.isVerifyingPhone}
            disabled={isVerifyingPhone || otpFields.join("").length < 4}
            onClick={this.handleSubmitOtp}
          >
            {configJSON.submitText}
          </CustomButton>
          <Box>
            <CustomGrayText>
              {configJSON.contactUsTextMessage}&nbsp;
              <ContactUsBtn data-test-id="contact-us-btn" onClick={this.handleContactUsRedirection}>
                {configJSON.contactUsText}
              </ContactUsBtn>
            </CustomGrayText>
          </Box>
        </ContentWrapper>
      </OTPDialogWrapper>
    );
    // Customizable Area End
  }
}

// Customizable Area Start
const OTPDialogWrapper = styled(Dialog)({
  "& .MuiPaper-root": {
    borderRadius: "20px",
    padding: "30px 40px 40px",
    width: "100%",
    maxWidth: "500px",
  },
});

const OTPCloseButton = styled(IconButton)({
  padding: "4px",
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.05)",
  },
});

const ContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const MainTitle = styled(Box)({
  fontSize: "1.75rem",
  fontWeight: 600,
  lineHeight: "32px",
  color: "#0F172A",
  marginBottom: "15px",
  textAlign: "center",
});

const ModalDescription = styled(Typography)({
  fontSize: "1rem",
  fontWeight: 500,
  lineHeight: "22px",
  color: "#64748B",
  marginBottom: "5px",
  textAlign: "center",
});

const FieldsContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "10px",
  maxWidth: "360px"
})

const CustomGrayText = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 500,
  lineHeight: "24px",
  color: "#64748B",
  textAlign: "center",
  whiteSpace: "pre-wrap",
});

const ResendBtn = styled(Button)({
  fontSize: "0.875rem",
  fontWeight: 500,
  lineHeight: "24px",
  color: "#652784",
  textTransform: "none",
  padding: "0px",
});

const OTPField = styled(TextField)({
  marginBottom: "5px",
  "& div": {
    height: "65px",
    width: "65px",
    border: "1px solid #CBD5E1",
    borderRadius: "16px",
    "@media (max-width: 425px)": {
      height: "50px",
      width: "50px",
    },
  },
  "& input": {
    fontSize: "1.5rem",
    fontWeight: "500",
    lineHeight: "32px",
    color: "#0F172A",
    textAlign: "center",
    fontFamily: "Montserrat, sans-serif !important",
  },
});

const ErrorText = styled(Typography)({
  color: "#E11B23",
  fontSize: "0.75rem",
  fontWeight: "500",
  textAlign: "center"
});

const ContactUsBtn = styled(Typography)({
  display: "inline",
  textDecoration: "underline",
  color: "#652784",
  fontSize: "0.875rem",
  fontWeight: 500,
  cursor: "pointer",
});
// Customizable Area End
